import { useCallback } from 'react';
import { useDeleteChatroomFromCache } from '#features/chatrooms';
import type { ChatroomDataObject, MessageDataObject } from '#models/services';
import { socketEvents } from '../../socket-events';
import { useSocketIo } from '../../useSocketIo';
import { useHandleSocketChatroomMembers } from './useHandleSocketChatroomMembers';
import { useHandleSocketChatroomMessage } from './useHandleSocketChatroomMessage';
import { useHandleSocketNewMessage } from './useHandleSocketNewMessage';

type UseChatSocketHandlersParams = {
  currentUserId: string;
  onCurrentUserRemovedFromChatroom?: () => void;
};

export const useChatSocketHandlers = ({
  currentUserId,
  onCurrentUserRemovedFromChatroom,
}: UseChatSocketHandlersParams) => {
  const handleSocketNewMessage = useHandleSocketNewMessage({ currentUserId });
  const handleSocketChatroomMessage = useHandleSocketChatroomMessage({ currentUserId });
  const handleSocketChatroomMembers = useHandleSocketChatroomMembers({
    currentUserId,
    onCurrentUserRemovedFromChatroom,
  });
  const deleteChatroomFromCache = useDeleteChatroomFromCache();

  const onNewMessage = useCallback(
    (socketData: MessageDataObject) => {
      handleSocketNewMessage(socketData);
      handleSocketChatroomMessage(socketData);
    },
    [handleSocketChatroomMessage, handleSocketNewMessage],
  );

  const onMemberCreated = useCallback(
    async (socketData: ChatroomDataObject) => {
      await handleSocketChatroomMembers({ action: 'add', socketData });
    },
    [handleSocketChatroomMembers],
  );

  const onMemberRemoved = useCallback(
    async (socketData: ChatroomDataObject) => {
      await handleSocketChatroomMembers({ action: 'remove', socketData });
    },
    [handleSocketChatroomMembers],
  );

  const onChatroomRemoved = useCallback(
    async (socketData: ChatroomDataObject) => {
      await deleteChatroomFromCache({ chatroomId: socketData._id });
    },
    [deleteChatroomFromCache],
  );

  useSocketIo<MessageDataObject>({
    event: socketEvents.chatroom.messages.created,
    onEvent: onNewMessage,
  });

  useSocketIo<ChatroomDataObject>({
    event: socketEvents.chatroom.members.created,
    onEvent: onMemberCreated,
  });

  useSocketIo<ChatroomDataObject>({
    event: socketEvents.chatroom.members.removed,
    onEvent: onMemberRemoved,
  });

  useSocketIo<ChatroomDataObject>({
    event: socketEvents.chatroom.removed,
    onEvent: onChatroomRemoved,
  });
};
